import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import api, { isOnboardingRelevantMutation } from '../services/api';
import { useAuth } from './AuthContext';

const OnboardingContext = createContext(null);
const clearTipSession = () => Object.keys(sessionStorage).forEach((key) => {
  if (key.startsWith('odontoassist:onboarding-tip-shown')) sessionStorage.removeItem(key);
});

export const useOnboarding = () => useContext(OnboardingContext);

export const OnboardingProvider = ({ children }) => {
  const { user, token, reloadMenuPreferences } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user || !token || user.role === 'admin' || user.clinic_role !== 'clinic_admin') {
      setState(null);
      return;
    }
    try {
      setLoading(true);
      const response = await api.get('/onboarding');
      setState(response.data);
    } catch (error) {
      console.warn('Onboarding indisponível:', error);
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const handleRefresh = () => window.setTimeout(refresh, 150);
    window.addEventListener('odontoassist:onboarding-refresh', handleRefresh);
    return () => window.removeEventListener('odontoassist:onboarding-refresh', handleRefresh);
  }, [refresh]);

  useEffect(() => {
    // Parte das telas legadas usa a instância global do Axios, fora de services/api.
    // Observa apenas mutações bem-sucedidas que podem concluir uma etapa.
    const interceptorId = axios.interceptors.response.use((response) => {
      if (isOnboardingRelevantMutation(response.config?.method, response.config?.url)) {
        window.dispatchEvent(new CustomEvent('odontoassist:onboarding-refresh'));
      }
      return response;
    });
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  const interaction = useCallback(async (interactionType, stepId = null) => {
    try {
      await api.post('/onboarding/interactions', { interaction_type: interactionType, step_id: stepId });
    } catch (error) {
      console.warn('Não foi possível registrar interação do onboarding:', error);
    }
  }, []);

  const start = useCallback(async (experienceProfile) => {
    const response = await api.post('/onboarding/start', {
      selected_goals: [experienceProfile],
      experience_profile: experienceProfile,
    });
    clearTipSession();
    await reloadMenuPreferences?.();
    setState(response.data);
    return response.data;
  }, [reloadMenuPreferences]);

  const updatePreferences = useCallback(async (preferences) => {
    if (preferences?.proactive_tips === true) {
      clearTipSession();
    }
    const response = await api.put('/onboarding/preferences', preferences);
    setState(response.data);
    return response.data;
  }, []);

  const resetTips = useCallback(async () => {
    clearTipSession();
    const response = await api.post('/onboarding/tips/reset');
    setState(response.data);
    return response.data;
  }, []);

  return <OnboardingContext.Provider value={{ state, loading, refresh, interaction, start, updatePreferences, resetTips }}>{children}</OnboardingContext.Provider>;
};
